import React, { useRef, useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  BarChart3, AlertCircle, FileText, Bell, Receipt, FileDiff, FileQuestion,
  ClipboardList, Package, Truck, Building2, Users, HardHat, Calendar,
  FileImage, AlertTriangle, ChevronRight, ChevronLeft, FolderOpen
} from 'lucide-react';

export default function ProjectTabBar({ activeTab, setActiveTab, canViewModule, project, projectId }) {
  const navigate = useNavigate();
  const tabsContainerRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const handleMouseDown = (e) => {
    if (!tabsContainerRef.current) return;
    setIsDragging(true);
    setStartX(e.pageX - tabsContainerRef.current.offsetLeft);
    setScrollLeft(tabsContainerRef.current.scrollLeft);
    tabsContainerRef.current.style.cursor = 'grabbing';
    tabsContainerRef.current.style.userSelect = 'none';
  };

  const handleMouseUp = () => {
    setIsDragging(false);
    if (tabsContainerRef.current) {
      tabsContainerRef.current.style.cursor = 'grab';
      tabsContainerRef.current.style.userSelect = '';
    }
  };

  const handleMouseMove = (e) => {
    if (!isDragging || !tabsContainerRef.current) return;
    e.preventDefault();
    const x = e.pageX - tabsContainerRef.current.offsetLeft;
    const walk = (x - startX) * 1.5;
    tabsContainerRef.current.scrollLeft = scrollLeft - walk;
  };

  const handleMouseLeave = () => {
    if (isDragging) {
      setIsDragging(false);
      if (tabsContainerRef.current) {
        tabsContainerRef.current.style.cursor = 'grab';
        tabsContainerRef.current.style.userSelect = '';
      }
    }
  };

  const checkScrollButtons = useCallback(() => {
    if (tabsContainerRef.current) {
      const { scrollLeft: sl, scrollWidth, clientWidth } = tabsContainerRef.current;
      setCanScrollLeft(sl > 5);
      setCanScrollRight(sl < scrollWidth - clientWidth - 5);
    }
  }, []);

  useEffect(() => {
    checkScrollButtons();
    const timer = setTimeout(checkScrollButtons, 100);
    const timer2 = setTimeout(checkScrollButtons, 500);
    const container = tabsContainerRef.current;
    if (container) {
      container.addEventListener('scroll', checkScrollButtons);
      window.addEventListener('resize', checkScrollButtons);
      return () => {
        clearTimeout(timer);
        clearTimeout(timer2);
        container.removeEventListener('scroll', checkScrollButtons);
        window.removeEventListener('resize', checkScrollButtons);
      };
    }
    return () => { clearTimeout(timer); clearTimeout(timer2); };
  }, [checkScrollButtons, activeTab]);

  const scrollTabs = (direction) => {
    if (tabsContainerRef.current) {
      tabsContainerRef.current.scrollBy({ left: direction === 'left' ? -200 : 200, behavior: 'smooth' });
    }
  };

  const contractFamily = project?.contract_type || project?.contract_family || '';
  const isNEC = contractFamily.startsWith('NEC');
  const isFIDIC = contractFamily === 'FIDIC';

  const handleMobileTab = (value) => {
    if (value === 'minutes') navigate(`/projects/${projectId}/meeting-minutes`);
    else if (value === 'shop-drawings') navigate(`/projects/${projectId}/shop-drawings`);
    else if (value === 'procurement') navigate(`/projects/${projectId}/procurement`);
    else if (value === 'irs') navigate(`/projects/${projectId}/irs`);
    else if (value === 'diary') navigate(`/projects/${projectId}/diary`);
    else if (value === 'plant') navigate(`/projects/${projectId}/plant`);
    else if (value === 'delay-detection') navigate(`/projects/${projectId}/delay-detection`);
    else setActiveTab(value);
  };

  const tabBtn = (key, label, Icon, opts = {}) => {
    const { module, onClick, borderColor = 'border-slate-900', textColor = 'text-slate-900', inactiveColor = 'text-slate-500 hover:text-slate-900' } = opts;
    if (module && !canViewModule(module)) return null;
    const isActive = activeTab === key;
    return (
      <button
        key={key}
        onClick={onClick || (() => setActiveTab(key))}
        className={`px-4 md:px-6 py-3 text-sm font-medium uppercase tracking-wide transition-colors whitespace-nowrap ${isActive ? `border-b-2 ${borderColor} ${textColor}` : inactiveColor}`}
        data-testid={`tab-${key}`}
        style={module ? { display: canViewModule(module) ? '' : 'none' } : undefined}
      >
        <Icon className="h-4 w-4 inline mr-2" />
        {label}
      </button>
    );
  };

  return (
    <>
      {/* Mobile Dropdown */}
      <div className="lg:hidden mb-6">
        <Select value={activeTab} onValueChange={handleMobileTab}>
          <SelectTrigger className="w-full" data-testid="mobile-tab-selector">
            <SelectValue placeholder="Select Module" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="overview"><span className="flex items-center gap-2"><BarChart3 className="h-4 w-4" /> Overview</span></SelectItem>
            <SelectItem value="awaiting"><span className="flex items-center gap-2"><AlertCircle className="h-4 w-4" /> Items Requiring Attention</span></SelectItem>
            {canViewModule('delay_events') && <SelectItem value="events"><span className="flex items-center gap-2"><FileText className="h-4 w-4" /> {isNEC ? 'Compensation Events' : isFIDIC ? 'Claims' : 'Delay Events'}</span></SelectItem>}
            {canViewModule('notices') && <SelectItem value="notices"><span className="flex items-center gap-2"><Bell className="h-4 w-4" /> {isNEC ? 'Early Warnings' : 'Notices'}</span></SelectItem>}
            {canViewModule('claims') && <SelectItem value="claims"><span className="flex items-center gap-2"><Receipt className="h-4 w-4" /> {isNEC ? 'Quotations' : 'Claims'}</span></SelectItem>}
            {canViewModule('variation_orders') && <SelectItem value="vos"><span className="flex items-center gap-2"><FileDiff className="h-4 w-4" /> Variation Orders</span></SelectItem>}
            {canViewModule('rfis') && <SelectItem value="rfi"><span className="flex items-center gap-2"><FileQuestion className="h-4 w-4" /> RFI</span></SelectItem>}
            {canViewModule('site_instructions') && <SelectItem value="si"><span className="flex items-center gap-2"><ClipboardList className="h-4 w-4" /> Site Instructions</span></SelectItem>}
            {canViewModule('meeting_minutes') && <SelectItem value="minutes"><span className="flex items-center gap-2"><FileText className="h-4 w-4" /> Meeting Minutes</span></SelectItem>}
            {canViewModule('shop_drawings') && <SelectItem value="shop-drawings"><span className="flex items-center gap-2"><Package className="h-4 w-4" /> Shop Drawings</span></SelectItem>}
            {canViewModule('procurement') && <SelectItem value="procurement"><span className="flex items-center gap-2"><Truck className="h-4 w-4" /> Procurement Schedule</span></SelectItem>}
            {canViewModule('irs') && <SelectItem value="irs"><span className="flex items-center gap-2"><ClipboardList className="h-4 w-4" /> Information Required</span></SelectItem>}
            <SelectItem value="professional"><span className="flex items-center gap-2"><Building2 className="h-4 w-4" /> Professional Team</span></SelectItem>
            <SelectItem value="internal"><span className="flex items-center gap-2"><Users className="h-4 w-4" /> Internal Team</span></SelectItem>
            <SelectItem value="subcontractors"><span className="flex items-center gap-2"><HardHat className="h-4 w-4" /> Sub-Contractors</span></SelectItem>
            <SelectItem value="milestones"><span className="flex items-center gap-2"><Calendar className="h-4 w-4" /> Key Milestones</span></SelectItem>
            {canViewModule('programs') && <SelectItem value="programs"><span className="flex items-center gap-2"><BarChart3 className="h-4 w-4" /> Programs</span></SelectItem>}
            {canViewModule('drawings') && <SelectItem value="drawings"><span className="flex items-center gap-2"><FileImage className="h-4 w-4" /> Drawings</span></SelectItem>}
            <SelectItem value="diary"><span className="flex items-center gap-2"><ClipboardList className="h-4 w-4" /> Site Diary</span></SelectItem>
            <SelectItem value="plant"><span className="flex items-center gap-2"><Truck className="h-4 w-4" /> Plant & Equipment</span></SelectItem>
            <SelectItem value="delay-detection"><span className="flex items-center gap-2"><AlertTriangle className="h-4 w-4" /> Delay Detection</span></SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Desktop Horizontal Tabs */}
      <div className="hidden lg:block border-b border-slate-200 mb-6 -mx-4 lg:-mx-8 px-4 lg:px-8">
        <div className="relative flex items-center">
          <button
            onClick={() => scrollTabs('left')}
            className={`absolute left-0 z-20 flex items-center justify-center w-10 h-10 bg-white border border-slate-200 rounded-full shadow-md hover:shadow-lg hover:bg-slate-50 transition-all ${canScrollLeft ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-4 pointer-events-none'}`}
            style={{ top: '50%', transform: canScrollLeft ? 'translateY(-50%)' : 'translateY(-50%) translateX(-1rem)' }}
            aria-label="Scroll tabs left"
          >
            <ChevronLeft className="h-5 w-5 text-slate-700" />
          </button>

          <div
            ref={tabsContainerRef}
            onMouseDown={handleMouseDown}
            onMouseUp={handleMouseUp}
            onMouseMove={handleMouseMove}
            onMouseLeave={handleMouseLeave}
            className="flex space-x-1 overflow-x-auto scrollbar-hide pb-px cursor-grab select-none"
            style={{ scrollBehavior: isDragging ? 'auto' : 'smooth' }}
            data-testid="tabs-scroll-container"
          >
            {tabBtn('overview', 'Overview', BarChart3)}
            <button
              onClick={() => navigate(`/projects/${projectId}/setup`)}
              className="px-4 md:px-6 py-3 text-sm font-medium uppercase tracking-wide transition-colors whitespace-nowrap text-slate-500 hover:text-slate-900"
              data-testid="tab-document-control"
            >
              <FolderOpen className="h-4 w-4 inline mr-2" />
              Setup & Documents
            </button>
            {tabBtn('awaiting', 'Items Requiring Attention', AlertCircle, { borderColor: 'border-red-600', textColor: 'text-red-600', inactiveColor: 'text-red-500 hover:text-red-700' })}
            {tabBtn('events', isNEC ? 'Compensation Events' : isFIDIC ? 'Claims' : 'Delay Events', FileText, { module: 'delay_events' })}
            {tabBtn('notices', isNEC ? 'Early Warnings' : 'Notices', Bell, { module: 'notices' })}
            {tabBtn('claims', isNEC ? 'Quotations' : 'Claims', Receipt, { module: 'claims' })}
            {tabBtn('vos', 'Variation Orders', FileDiff, { module: 'variation_orders' })}
            {tabBtn('rfi', 'RFI', FileQuestion, { module: 'rfis' })}
            {tabBtn('si', 'Site Instructions', ClipboardList, { module: 'site_instructions' })}
            {tabBtn('minutes', 'Minutes', FileText, { module: 'meeting_minutes' })}
            {tabBtn('shopDrawings', 'Shop Drawings', Package, { module: 'shop_drawings' })}
            {tabBtn('procurement', 'Procurement', Truck, { module: 'procurement', onClick: () => navigate(`/projects/${projectId}/procurement`) })}
            {tabBtn('irs', 'Information Required', ClipboardList, { module: 'irs', onClick: () => navigate(`/projects/${projectId}/irs`) })}
            {tabBtn('professional', 'Professional Team', Building2)}
            {tabBtn('internal', 'Internal Team', Users)}
            {tabBtn('subcontractors', 'Sub-Contractors', HardHat)}
            {tabBtn('milestones', 'Key Milestones', Calendar)}
            {tabBtn('programs', 'Programs', BarChart3, { module: 'programs' })}
            {tabBtn('drawings', 'Drawings', FileImage, { module: 'drawings' })}
            <button
              onClick={() => navigate(`/projects/${projectId}/diary`)}
              className={`px-4 md:px-6 py-3 text-sm font-medium uppercase tracking-wide transition-colors whitespace-nowrap ${activeTab === 'diary' ? 'border-b-2 border-slate-900 text-slate-900' : 'text-slate-500 hover:text-slate-900'}`}
              data-testid="tab-diary"
            >
              <ClipboardList className="h-4 w-4 inline mr-2" />
              Site Diary
            </button>
            <button
              onClick={() => navigate(`/projects/${projectId}/plant`)}
              className={`px-4 md:px-6 py-3 text-sm font-medium uppercase tracking-wide transition-colors whitespace-nowrap ${activeTab === 'plant' ? 'border-b-2 border-slate-900 text-slate-900' : 'text-slate-500 hover:text-slate-900'}`}
              data-testid="tab-plant"
            >
              <Truck className="h-4 w-4 inline mr-2" />
              Plant & Equipment
            </button>
            <button
              onClick={() => navigate(`/projects/${projectId}/delay-detection`)}
              className={`px-4 md:px-6 py-3 text-sm font-medium uppercase tracking-wide transition-colors whitespace-nowrap ${activeTab === 'delay-detection' ? 'border-b-2 border-slate-900 text-slate-900' : 'text-slate-500 hover:text-slate-900'}`}
              data-testid="tab-delay-detection"
            >
              <AlertTriangle className="h-4 w-4 inline mr-2" />
              Delay Detection
            </button>
          </div>

          <button
            onClick={() => scrollTabs('right')}
            className={`absolute right-0 z-20 flex items-center justify-center w-10 h-10 bg-white border border-slate-200 rounded-full shadow-md hover:shadow-lg hover:bg-slate-50 transition-all ${canScrollRight ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-4 pointer-events-none'}`}
            style={{ top: '50%', transform: canScrollRight ? 'translateY(-50%)' : 'translateY(-50%) translateX(1rem)' }}
            aria-label="Scroll tabs right"
          >
            <ChevronRight className="h-5 w-5 text-slate-700" />
          </button>
        </div>
      </div>
    </>
  );
}
